/**
 * Ratio & Proportion: ratio notation, solving ratio/proportion problems, sharing in a given ratio.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const gcd = (a, b) => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y) { [x, y] = [y, x % y]; }
    return x || 1;
};

const makeDistractors = (correctStr, count = 3, numeric = false) => {
    const seen = new Set([correctStr]);
    const out = [];
    if (numeric) {
        const num = Number(correctStr);
        [1, 2, 3, 5, 10].forEach((off) => {
            const s = String(num + off);
            if (s !== correctStr && !seen.has(s) && out.length < count) { seen.add(s); out.push(s); }
        });
        while (out.length < count) {
            const s = String(num + Math.floor(Math.random() * 20) - 10);
            if (s !== correctStr && !seen.has(s)) { seen.add(s); out.push(s); }
        }
        return out.slice(0, count);
    }
    const ratioMatch = correctStr.match(/^(\d+):(\d+)$/);
    if (ratioMatch) {
        const a = Number(ratioMatch[1]);
        const b = Number(ratioMatch[2]);
        [[a + 1, b], [a, b + 1], [a + 1, b + 1], [Math.max(1, a - 1), b], [a, Math.max(1, b - 1)]].forEach(([x, y]) => {
            const s = `${x}:${y}`;
            if (s !== correctStr && !seen.has(s) && out.length < count) { seen.add(s); out.push(s); }
        });
        return out.slice(0, count);
    }
    return out;
};

/** Understanding ratio notation – simplify ratio, equivalent ratio */
export const generateRatioNotationQuestion = (difficulty = "normal") => {
    let a = Math.floor(Math.random() * 8) + 2;
    let b = Math.floor(Math.random() * 8) + 2;
    const g = gcd(a, b);
    while (g <= 1) {
        a = Math.floor(Math.random() * 8) + 2;
        b = Math.floor(Math.random() * 8) + 2;
    }
    const na = a / g;
    const nb = b / g;
    const answer = `${na}:${nb}`;
    const q = `Simplify to the simplest form:`;
    const options = shuffle([answer, ...makeDistractors(answer, 3, false)]);
    return {
        question: q,
        ratio: { a, b },
        simplified: { a: na, b: nb },
        answer,
        options,
        type: "ratio-notation",
        mode: "notation",
        visualType: "ratio-bar",
    };
};

/** Solving problems using ratios and proportion */
export const generateRatioProportionQuestion = (difficulty = "normal") => {
    const templates = [
        { q: (a, b, c) => `If ${a} oranges cost ${b}p, how much do ${c} oranges cost?`, ans: (a, b, c) => Math.round((b / a) * c) },
        { q: (a, b, c) => `A recipe uses ${a}:${b} flour to sugar. For ${c} parts flour, how many parts sugar?`, ans: (a, b, c) => Math.round((b / a) * c) },
        { q: (a, b, c) => `${a} books weigh ${b} kg. How much do ${c} books weigh?`, ans: (a, b, c) => Math.round((b / a) * c * 10) / 10 },
    ];
    const a = difficulty === "easy" ? Math.floor(Math.random() * 4) + 2 : Math.floor(Math.random() * 6) + 3;
    const b = difficulty === "easy" ? a * (Math.floor(Math.random() * 3) + 2) : Math.floor(Math.random() * 30) + 10;
    const c = difficulty === "easy" ? a + Math.floor(Math.random() * 3) + 1 : a + Math.floor(Math.random() * 8) + 2;
    const t = templates[Math.floor(Math.random() * templates.length)];
    const answer = t.ans(a, b, c);
    const ansStr = Number.isInteger(answer) ? String(answer) : String(answer);
    const q = t.q(a, b, c);
    const options = shuffle([ansStr, ...makeDistractors(ansStr, 3, true)]);
    return {
        question: q,
        proportionData: { a, b, c },
        answer: ansStr,
        options,
        type: "ratio-proportion-problems",
        mode: "proportion",
        visualType: "proportion-chart",
    };
};

/** Sharing quantities in a given ratio */
export const generateSharingInRatioQuestion = (difficulty = "normal") => {
    const total = difficulty === "easy" ? [10, 15, 20][Math.floor(Math.random() * 3)] : difficulty === "hard" ? Math.floor(Math.random() * 41) + 20 : Math.floor(Math.random() * 26) + 15;
    let p = Math.floor(Math.random() * 4) + 2;
    let q = Math.floor(Math.random() * 4) + 2;
    if (p === q) q += 1;
    const parts = p + q;
    const onePart = total / parts;
    const firstShare = Math.round(p * onePart);
    const secondShare = Math.round(q * onePart);
    const which = Math.random() < 0.5 ? "first" : "second";
    const answer = which === "first" ? firstShare : secondShare;
    const qText = `Share ${total} in the ratio ${p}:${q}. What is the ${which === "first" ? "first" : "second"} share?`;
    const options = shuffle([String(answer), ...makeDistractors(String(answer), 3, true)]);
    return {
        question: qText,
        total,
        ratio: [p, q],
        shares: [firstShare, secondShare],
        answer: String(answer),
        options,
        type: "sharing-in-ratio",
        mode: "sharing",
        visualType: "sharing-bar",
    };
};

export const generateRatioProportionQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "ratio-notation":
            return generateRatioNotationQuestion(difficulty);
        case "ratio-proportion-problems":
            return generateRatioProportionQuestion(difficulty);
        case "sharing-in-ratio":
            return generateSharingInRatioQuestion(difficulty);
        default:
            return generateRatioNotationQuestion(difficulty);
    }
};
